<template>
  <div class="host-overview">
    <section class="host-overview__intro">
      <article class="host-story-card">
        <span class="host-story-card__eyebrow">Resumen</span>
        <h2>{{ formatMoney(reportRevenue) }}</h2>
        <p>{{ storyLead }}</p>

        <div class="host-story-card__pills">
          <span><i class="fas fa-ticket-alt"></i>{{ reportTickets }} tickets emitidos</span>
          <span><i class="fas fa-gift"></i>{{ reportCourtesies }} cortesías</span>
          <span><i class="fas fa-layer-group"></i>{{ stageRows.length }} etapas con movimiento</span>
        </div>
      </article>

      <article class="host-story-card host-story-card--compact">
        <span class="host-story-card__eyebrow">Etapa líder</span>
        <strong>{{ topSellingTicket.name }}</strong>
        <p>{{ topSellingTicket.copy }}</p>
        <div class="host-story-card__footer">{{ topSellingTicket.metric }}</div>
      </article>
    </section>

    <section class="host-overview__metrics">
      <article v-for="cardItem in metricCards" :key="cardItem.label" class="host-stat-card">
        <small>{{ cardItem.label }}</small>
        <strong>{{ cardItem.value }}</strong>
        <span>{{ cardItem.caption }}</span>
      </article>
    </section>

    <section class="host-overview__grid">
      <article class="host-panel host-panel--chart">
        <div class="host-panel__header">
          <div>
            <span class="host-panel__eyebrow">Cobros</span>
            <h3>Cómo entra el dinero</h3>
            <p>Primero mira la caja del evento. Este bloque sigue al frente para que entiendas rápido cómo se está cobrando.</p>
          </div>

          <button class="host-detail-button" type="button" @click="paymentMixExpanded = !paymentMixExpanded">
            {{ paymentMixExpanded ? 'Vista compacta' : 'Ver detalle' }}
          </button>
        </div>

        <div :class="['host-panel__chart-shell', 'host-panel__chart-shell--bar', { 'is-expanded': paymentMixExpanded }]">
          <Bar :data="paymentMixData" :options="barOptions" />
        </div>
      </article>

      <article class="host-panel host-panel--signals">
        <div class="host-panel__header">
          <div>
            <span class="host-panel__eyebrow">Radar</span>
            <h3>Señales clave del evento</h3>
            <p>Un pulso rápido para leer facturación, ritmo y salud comercial sin salir del resumen.</p>
          </div>
        </div>

        <div class="host-signal-grid">
          <article v-for="signal in signalCards" :key="signal.label" class="host-signal-card">
            <small>{{ signal.label }}</small>
            <strong>{{ signal.value }}</strong>
            <span>{{ signal.caption }}</span>
          </article>
        </div>
      </article>
    </section>

    <section class="host-panel host-panel--daily">
      <div class="host-panel__header host-panel__header--daily">
        <div>
          <span class="host-panel__eyebrow">Ventas diarias</span>
          <h3>Avance diario de ventas</h3>
          <p>Revisa el comportamiento diario del evento o sigue el acumulado.</p>
        </div>

        <div class="host-tabs">
          <button :class="['host-tabs__button', { 'is-active': dailyMode === 'cumulative' }]" type="button" @click="dailyMode = 'cumulative'">Acumulado</button>
          <button :class="['host-tabs__button', { 'is-active': dailyMode === 'daily' }]" type="button" @click="dailyMode = 'daily'">Por día</button>
        </div>
      </div>

      <div v-if="loadingDaily" class="host-empty">Cargando avance diario...</div>
      <div v-else-if="!dailyItems.length" class="host-empty">Todavía no hay ventas o pendientes para mostrar aquí.</div>
      <div v-else class="host-daily">
        <div class="host-panel__chart-shell host-panel__chart-shell--trend">
          <component :is="dailyChartComponent" :data="dailyChartData" :options="dailyChartOptions" />
        </div>

        <aside class="host-daily__aside">
          <article class="host-daily__stat">
            <small>Tickets aprobados</small>
            <strong>{{ dailySummary.approved_tickets || 0 }}</strong>
            <span>{{ formatMoney(dailySummary.approved_revenue) }} en ventas aprobadas</span>
          </article>
          <article class="host-daily__stat">
            <small>Mejor día</small>
            <strong>{{ peakDayLabel }}</strong>
            <span>{{ peakDayValue }}</span>
          </article>
          <article class="host-daily__stat">
            <small>Último movimiento</small>
            <strong>{{ latestDayLabel }}</strong>
            <span>{{ latestDayValue }}</span>
          </article>
        </aside>
      </div>

      <div v-if="dailyItems.length" class="host-table-wrap">
        <table class="host-table">
          <thead>
            <tr>
              <th>Día</th>
              <th>Tickets emitidos</th>
              <th>Cortesías</th>
              <th>Pendientes</th>
              <th>Facturado</th>
              <th>{{ dailyMode === 'cumulative' ? 'Acumulado' : 'Importe por ticket' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in dailyItems" :key="row.date">
              <td>{{ formatDateLabel(row.date) }}</td>
              <td>{{ row.sold }}</td>
              <td>{{ row.courtesies }}</td>
              <td>{{ row.pending }}</td>
              <td>{{ formatMoney(row.revenue) }}</td>
              <td>{{ dailyMode === 'cumulative' ? row.cumulative_sold : formatMoney(row.average_ticket) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="host-panel">
      <div class="host-panel__header host-panel__header--daily">
        <div>
          <span class="host-panel__eyebrow">Leaderboard</span>
          <h3>Ventas por código y etapa</h3>
          <p>Lo mejor del movimiento comercial primero. Revisa el top y abre el detalle completo solo cuando lo necesites.</p>
        </div>

        <div class="host-tabs">
          <button :class="['host-tabs__button', { 'is-active': reportTab === 'Ventas x Código' }]" type="button" @click="reportTab = 'Ventas x Código'">Ventas x Código</button>
          <button :class="['host-tabs__button', { 'is-active': reportTab === 'Ventas x Etapa' }]" type="button" @click="reportTab = 'Ventas x Etapa'">Ventas x Etapa</button>
        </div>
      </div>

      <div class="host-panel__actions">
        <button class="host-detail-button" type="button" @click="inventoryExpanded = !inventoryExpanded">
          {{ inventoryExpanded ? 'Mostrar menos' : 'Ver todos' }}
        </button>
      </div>

      <p v-if="loadingSalesReport" class="host-empty" role="status">Cargando ventas confirmadas…</p>
      <p v-else-if="salesReportError" class="host-empty" role="alert">{{ salesReportError }} <button class="host-detail-button" @click="loadSalesReport">Reintentar</button></p>
      <SalesReportTable v-else :rows="reportTab === 'Ventas x Código' ? visibleTicketRows : visibleStageRows" :group="reportTab === 'Ventas x Código' ? 'code' : 'stage'" :event-id="event.id" :currency="event.presentment_currency || 'USD'" />
      <p v-if="salesReport && salesReport.summary.historical_invoices" class="host-panel__hint">Hay compras anteriores sin todos los datos originales. N/D identifica cantidades comerciales que no se pueden reconstruir con certeza.</p>
      <p v-if="salesReport && Number(salesReport.summary.unallocated_revenue)" class="host-panel__hint">{{ formatMoney(salesReport.summary.unallocated_revenue) }} incluidos en el total del evento tienen desglose histórico por etapa/código pendiente de conciliación.</p>
      <p v-if="salesReport && salesReport.summary.cross_event_unallocated_invoices" class="host-panel__hint">Hay facturas antiguas con varios eventos sin desglose recuperable; sus importes requieren conciliación.</p>
      <p v-if="salesReport && Number(salesReport.summary.difference)" class="host-panel__hint">Diferencia entre facturado y cobros aprobados: {{ formatMoney(salesReport.summary.difference) }}.</p>
    </section>

    <section class="host-panel host-panel--traffic-report">
      <div class="host-panel__header">
        <div>
          <span class="host-panel__eyebrow">Tráfico</span>
          <h3>Lectura de atracción del landing</h3>
          <p>Cuántas visitas llegan, cuántas son únicas y qué códigos realmente están moviendo tráfico y compras.</p>
        </div>

        <button class="host-detail-button" type="button" @click="trafficReportExpanded = !trafficReportExpanded">
          {{ trafficReportExpanded ? 'Vista compacta' : 'Ver detalle' }}
        </button>
      </div>

      <div v-if="loadingTraffic" class="host-empty">Cargando tráfico del landing...</div>
      <div v-else class="host-traffic-report">
        <div class="host-signal-grid host-signal-grid--traffic">
          <article class="host-signal-card">
            <small>Visitas y usos de código</small>
            <strong>{{ trafficSummary.impressions || 0 }}</strong>
            <span>{{ trafficSummary.landing_visits || 0 }} visitas y {{ trafficSummary.manual_code_uses || 0 }} usos manuales. Una entrada por sesión, código y día.</span>
          </article>
          <article class="host-signal-card">
            <small>Visitantes únicos</small>
            <strong>{{ trafficSummary.unique_visitors || 0 }}</strong>
            <span>Navegadores distintos detectados en el landing.</span>
          </article>
          <article class="host-signal-card">
            <small>Compras atribuidas</small>
            <strong>{{ trafficSummary.attributed_orders || 0 }}</strong>
            <span>Órdenes confirmadas con código comercial.</span>
          </article>
          <article class="host-signal-card">
            <small>Conversión</small>
            <strong>{{ trafficConversionLabel }}</strong>
            <span>Visitantes vinculados a una compra confirmada. N/D si falta vínculo histórico.</span>
          </article>
        </div>

        <div class="host-table-wrap" v-if="visibleTrafficLeaderboard.length">
          <table class="host-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Ambassador</th>
                <th>Visitas</th>
                <th>Únicos</th>
                <th>Compras</th>
                <th>Tickets</th>
                <th>Facturado</th>
                <th>Conversión</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in visibleTrafficLeaderboard" :key="row.key || row.code">
                <td>{{ row.label || row.code }}</td>
                <td>{{ row.ambassador_name || 'Sin asignar' }}</td>
                <td>{{ row.impressions }}</td>
                <td>{{ row.unique_visitors }}</td>
                <td>{{ row.orders }}</td>
                <td>{{ row.tickets }}</td>
                <td>{{ formatMoney(row.revenue) }}</td>
                <td>{{ formatPercent(row.conversion) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p v-else class="host-empty">Todavía no hay tráfico suficiente para mostrar el leaderboard.</p>
      </div>

      <p v-if="!trafficReportExpanded && trafficLeaderboard.length > previewLimit" class="host-panel__hint">Mostrando los primeros códigos. Abre el detalle para ver todos.</p>
    </section>

    <section class="host-panel host-panel--split">
      <div class="host-panel__header">
        <div>
          <span class="host-panel__eyebrow">Origen</span>
          <h3>De dónde viene la compra</h3>
          <p>Lo dejamos al final para que primero manden caja, ventas y operación. Aquí haces lectura fina de atribución.</p>
        </div>

        <button class="host-detail-button" type="button" @click="trafficExpanded = !trafficExpanded">
          {{ trafficExpanded ? 'Vista compacta' : 'Ver detalle' }}
        </button>
      </div>

      <div class="host-traffic">
        <div :class="['host-panel__chart-shell', 'host-panel__chart-shell--doughnut', { 'is-expanded': trafficExpanded }]">
          <Doughnut :data="trafficChartData" :options="doughnutOptions" />
        </div>

        <div class="host-traffic__list">
          <article v-for="row in visibleTrafficRows" :key="row.label" class="host-traffic__item">
            <div>
              <strong>{{ row.label }}</strong>
              <span>{{ row.note }}</span>
            </div>
            <b>{{ row.value }}</b>
          </article>
        </div>
      </div>

      <p v-if="!trafficExpanded && trafficRows.length > previewLimit" class="host-panel__hint">Mostrando las fuentes principales. El detalle completo sigue disponible.</p>
    </section>
  </div>
</template>

<script>
import { apiService } from '../../common/api.services.js'
import SalesReportTable from '../../components/host/SalesReportTable.vue'
import { Bar, Doughnut, Line } from 'vue-chartjs'
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Filler, Legend, LineElement, LinearScale, PointElement, Title, Tooltip } from 'chart.js'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement, LineElement, PointElement, Filler)

export default {
  name: 'TestView',

  components: {
    SalesReportTable,
    Bar,
    Doughnut,
    Line,
  },

  props: {
    tickets: { required: true },
    sales: { required: true },
    courtesies: { required: true },
    eventTickets: { required: true },
    generatedData: { required: true },
    generatedData2: { required: true },
    total_tickets: { required: true },
    card: { required: true },
    cardPos: { required: true },
    bank: { required: true },
    cash: { required: true },
    event: { required: true },
  },

  data() {
    return {
      salesReport: null,
      loadingSalesReport: false,
      salesReportError: '',
      salesReportRequest: 0,
      reportTab: 'Ventas x Código',
      dailyMode: 'cumulative',
      loadingStages: false,
      dailyItems: [],
      dailySummary: {},
      loadingDaily: false,
      loadingTraffic: false,
      inventoryExpanded: false,
      trafficExpanded: false,
      trafficReportExpanded: false,
      paymentMixExpanded: false,
      previewLimit: 5,
      trafficSummary: {},
      trafficLeaderboard: [],
    }
  },

  computed: {
    reportRevenue() { return this.salesReport ? this.salesReport.summary.revenue : this.sales },
    reportTickets() { return this.salesReport ? this.salesReport.summary.tickets : this.tickets },
    reportCourtesies() { return this.salesReport ? this.salesReport.summary.courtesies : this.courtesies },
    storyLead() {
      if ((this.dailySummary && this.dailySummary.peak_day) || this.reportTickets > 0) {
        const peak = this.dailySummary && this.dailySummary.peak_day
        if (peak) {
        return `Tu mejor día fue ${this.formatDateLabel(peak.date)} con ${peak.sold} tickets y ${this.formatMoney(peak.revenue)} en ventas.`
        }

        return `El evento ya suma ${this.reportTickets} tickets aprobados y ${this.formatMoney(this.reportRevenue)} en ventas.`
      }

      return 'Todavía no hay ventas aprobadas, pero aquí verás el movimiento apenas entren las primeras órdenes.'
    },

    topSellingTicket() {
      const winner = [...this.stageRows].sort((a, b) => b.tickets - a.tickets)[0]
      return winner ? { name: winner.name, copy: `${winner.tickets} tickets emitidos por ventas.`, metric: this.formatMoney(winner.revenue) } : { name: 'Aún sin etapa líder', copy: 'Esperando ventas confirmadas.', metric: 'Sin actividad' }
    },

    metricCards() {
      return [
        { label: 'Ventas', value: this.salesReport && this.salesReport.summary.sales != null ? this.salesReport.summary.sales : 'N/D', caption: 'Unidades comerciales confirmadas' },
        { label: 'Compras', value: this.salesReport ? this.salesReport.summary.orders : 0, caption: 'Órdenes confirmadas distintas' },
        {
          label: 'Facturado',
          value: this.formatMoney(this.reportRevenue),
          caption: 'Solo ticketing aprobado',
        },
        {
          label: 'Tickets emitidos',
          value: this.reportTickets,
          caption: 'Entradas confirmadas por ventas',
        },
        {
          label: 'Cortesías',
          value: this.reportCourtesies,
          caption: 'Invitaciones y accesos de cortesía',
        },
        {
          label: 'Pagos pendientes',
          value: this.salesReport ? this.salesReport.summary.pending : this.dailySummary.pending_tickets || 0,
          caption: 'Tickets aún por validar',
        },
      ]
    },

    signalCards() {
      const sold = Number(this.reportTickets || 0)
      const courtesies = Number(this.reportCourtesies || 0)
      const totalAccesses = sold + courtesies
      const courtesyShare = totalAccesses > 0 ? `${Math.round((courtesies / totalAccesses) * 100)}%` : '0%'

      return [
        {
          label: 'Facturado ticketing',
          value: this.formatMoney(this.reportRevenue),
          caption: 'Importe de entradas confirmadas',
        },
        {
          label: 'Ritmo de cierre',
          value: this.peakDayLabel,
          caption: this.peakDayValue,
        },
        {
          label: 'Peso de cortesía',
          value: courtesyShare,
          caption: `${courtesies} cortesías sobre ${totalAccesses} accesos emitidos`,
        },
      ]
    },

    paymentMixData() {
      const methods = this.salesReport ? this.salesReport.summary.payment_methods : null
      return {
        labels: methods ? methods.map(method => method.label) : ['Tarjeta', 'Transferencia', 'Efectivo', 'POS'],
        datasets: [
          {
            borderRadius: 16,
            backgroundColor: ['#8fe2dc', '#79aee4', '#f0c17a', '#d7e4ff', '#bd9be8', '#c9d7a0'],
            data: methods ? methods.map(method => Number(method.amount)) : [this.card, this.bank, this.cash, this.cardPos].map((value) => Number.parseFloat(value || 0)),
          },
        ],
      }
    },

    trafficChartData() {
      return this.generatedData2 || { labels: [], datasets: [{ data: [] }] }
    },

    trafficRows() {
      const dataset = (this.generatedData2 && this.generatedData2.datasets && this.generatedData2.datasets[0]) || { data: [] }
      const values = dataset.data || []
      return [
        { label: 'Meta / IG', value: values[0] || 0, note: 'FB e Instagram' },
        { label: 'TikTok', value: values[1] || 0, note: 'Contenido de video corto' },
        { label: 'Mail', value: values[2] || 0, note: 'Base y newsletters' },
        { label: 'Promotores', value: values[3] || 0, note: 'Embajadores y hosts' },
        { label: 'Sitio web', value: values[4] || 0, note: 'Venta organica o directa' },
      ]
    },

    visibleTrafficRows() {
      return this.trafficExpanded ? this.trafficRows : this.trafficRows.slice(0, this.previewLimit)
    },

    visibleTrafficLeaderboard() {
      return this.trafficReportExpanded ? this.trafficLeaderboard : this.trafficLeaderboard.slice(0, this.previewLimit)
    },

    codeRows() { return this.salesReport ? this.salesReport.codes : [] },
    visibleTicketRows() { return this.inventoryExpanded ? this.codeRows : this.codeRows.slice(0, this.previewLimit) },
    stageRows() { return this.salesReport ? this.salesReport.stages : [] },
    visibleStageRows() { return this.inventoryExpanded ? this.stageRows : this.stageRows.slice(0, this.previewLimit) },

    trafficConversionLabel() {
      return this.formatPercent(this.trafficSummary.conversion)
    },

    dailyChartComponent() {
      return this.dailyMode === 'cumulative' ? Line : Bar
    },

    dailyChartData() {
      const labels = this.dailyItems.map((item) => this.formatShortDate(item.date))

      if (this.dailyMode === 'cumulative') {
        return {
          labels,
          datasets: [
            {
              label: 'Tickets acumulados',
              data: this.dailyItems.map((item) => item.cumulative_sold),
              borderColor: '#8fe2dc',
              backgroundColor: 'rgba(143, 226, 220, 0.16)',
              fill: true,
              tension: 0.34,
            },
          ],
        }
      }

      return {
        labels,
        datasets: [
          {
            label: 'Tickets por día',
            data: this.dailyItems.map((item) => item.sold),
            backgroundColor: '#8bbce2',
            borderRadius: 16,
            borderSkipped: false,
          },
        ],
      }
    },

    dailyChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: {
              color: '#d8e1f4',
            },
          },
        },
        scales: {
          x: {
            ticks: { color: 'rgba(210, 220, 238, 0.72)' },
            grid: { color: 'rgba(255, 255, 255, 0.04)' },
          },
          y: {
            ticks: { color: 'rgba(210, 220, 238, 0.72)', precision: 0 },
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
          },
        },
      }
    },

    doughnutOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              color: '#d8e1f4',
              padding: 18,
            },
          },
        },
      }
    },

    barOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          x: {
            ticks: { color: 'rgba(210, 220, 238, 0.72)' },
            grid: { display: false },
          },
          y: {
            ticks: { color: 'rgba(210, 220, 238, 0.72)' },
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
          },
        },
      }
    },

    peakDayLabel() {
      const peakDay = this.dailySummary.peak_day
      return peakDay ? this.formatDateLabel(peakDay.date) : 'Sin pico'
    },

    peakDayValue() {
      const peakDay = this.dailySummary.peak_day
      return peakDay ? `${peakDay.sold} tickets · ${this.formatMoney(peakDay.revenue)}` : 'Todavía sin ventas aprobadas'
    },

    latestDayLabel() {
      const latestDay = this.dailySummary.latest_day
      return latestDay ? this.formatDateLabel(latestDay.date) : 'Sin movimiento'
    },

    latestDayValue() {
      const latestDay = this.dailySummary.latest_day
      return latestDay ? `${latestDay.sold} tickets · ${latestDay.pending} pendientes` : 'Aún no hay registros'
    },
  },

  created() {
    this.loadDailySales()
    this.loadSalesReport()
  },

  watch: {
    'event.id'(value, previousValue) {
      if (value && value !== previousValue) {
        this.loadDailySales()
        this.loadSalesReport()
      }
    },
  },

  methods: {
    async loadSalesReport() {
      if (!this.event || !this.event.id) return
      const version = ++this.salesReportRequest
      this.loadingSalesReport = true; this.salesReportError = ''; this.salesReport = null
      try {
        const data = await apiService(`/api/host/events/${this.event.id}/sales-report/`)
        if (version !== this.salesReportRequest) return
        if (!data || !data.summary) throw new Error(data && data.err || 'No pudimos cargar el reporte de ventas.')
        this.salesReport = data
        this.trafficSummary = data.summary
        this.trafficLeaderboard = data.codes.filter(row => row.code)
      } catch (error) { if (version === this.salesReportRequest) this.salesReportError = error.message }
      finally { if (version === this.salesReportRequest) this.loadingSalesReport = false }
    },

    loadDailySales() {
      this.loadingDaily = true
      apiService(`/api/host/events/${this.event.id}/daily-sales/`).then((data) => {
        this.dailyItems = data && Array.isArray(data.items) ? data.items : []
        this.dailySummary = data && data.summary ? data.summary : {}
      }).finally(() => {
        this.loadingDaily = false
      })
    },

    loadTrafficSummary() {
      if (!this.event || !this.event.id) {
        return
      }

      this.loadingTraffic = true
      apiService(`/api/host/events/${this.event.id}/traffic-summary/`).then((data) => {
        this.trafficSummary = data && data.summary ? data.summary : {}
        this.trafficLeaderboard = data && Array.isArray(data.top_codes) ? data.top_codes : []
      }).finally(() => {
        this.loadingTraffic = false
      })
    },

    formatMoney(value) {
      const parsed = Number.parseFloat(value || 0)
      const currency = String(this.event && this.event.presentment_currency || 'ARS').toUpperCase()
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 2,
      }).format(Number.isNaN(parsed) ? 0 : parsed)
    },

    formatPercent(value) {
      if (value == null) return 'N/D'
      const parsed = Number.parseFloat(value || 0)
      return `${Number.isNaN(parsed) ? '0.0' : parsed.toFixed(1)}%`
    },

    normalizeCodeKey(value) {
      return String(value || '').trim().toUpperCase()
    },

    formatDateLabel(value) {
      if (!value) {
        return 'Sin fecha'
      }

      return new Intl.DateTimeFormat('es-AR', { dateStyle: 'medium' }).format(new Date(`${value}T00:00:00`))
    },

    formatShortDate(value) {
      if (!value) {
        return '--'
      }

      return new Intl.DateTimeFormat('es-AR', { month: 'short', day: 'numeric' }).format(new Date(`${value}T00:00:00`))
    },

    ticketShare(ticket) {
      const sold = Number(ticket && ticket.tkt ? ticket.tkt : 0)
      const total = Number(this.reportTickets || 0)
      if (total <= 0) {
        return '0%'
      }

      return `${Math.round((sold / total) * 100)}%`
    },
  },
}
</script>

<style scoped>
.host-overview {
  display: grid;
  gap: 20px;
}

.host-overview__intro {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(300px, 0.9fr);
  gap: 16px;
}

.host-story-card,
.host-story-card--compact {
  position: relative;
  overflow: hidden;
  display: grid;
  gap: 12px;
  padding: 22px;
  border-radius: 30px;
  background:
    radial-gradient(circle at 14% 0%, rgba(208, 218, 236, 0.1), transparent 24%),
    radial-gradient(circle at 84% 14%, rgba(92, 71, 130, 0.16), transparent 22%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.008)),
    rgba(7, 8, 13, 0.96);
  border: 1px solid rgba(196, 206, 228, 0.12);
  box-shadow: 0 26px 58px rgba(0, 0, 0, 0.3);
}

.host-story-card__eyebrow {
  color: #cfd8ea;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.host-story-card h2,
.host-story-card--compact strong {
  margin: 0;
  color: #f4f7ff;
  font-family: 'Syne', sans-serif;
}

.host-story-card h2 {
  font-size: clamp(2.4rem, 3.8vw, 4rem);
  line-height: 0.9;
}

.host-story-card p,
.host-story-card--compact p,
.host-story-card__footer {
  margin: 0;
  color: rgba(204, 214, 232, 0.82);
  line-height: 1.6;
}

.host-story-card__pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.host-story-card__pills span,
.host-story-card__footer {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(196, 206, 228, 0.1);
}

.host-story-card--compact strong {
  font-size: clamp(1.5rem, 2.1vw, 2.2rem);
  line-height: 0.96;
}

.host-overview__metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.host-stat-card,
.host-panel {
  position: relative;
  overflow: hidden;
  border-radius: 28px;
  background:
    radial-gradient(circle at 14% 0%, rgba(208, 218, 236, 0.08), transparent 26%),
    radial-gradient(circle at 84% 14%, rgba(92, 71, 130, 0.14), transparent 22%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.008)),
    rgba(7, 8, 13, 0.96);
  border: 1px solid rgba(196, 206, 228, 0.12);
  box-shadow: 0 24px 56px rgba(0, 0, 0, 0.28);
}

.host-stat-card {
  display: grid;
  gap: 8px;
  padding: 18px;
}

.host-stat-card small,
.host-panel__eyebrow,
.host-daily__stat small {
  color: #cfd8ea;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.host-stat-card strong,
.host-daily__stat strong {
  color: #f4f7ff;
  font-size: clamp(1.6rem, 2.2vw, 2.4rem);
  line-height: 0.96;
  font-family: 'Space Grotesk', sans-serif;
}

.host-stat-card span,
.host-daily__stat span,
.host-panel__header p {
  color: rgba(198, 207, 226, 0.8);
  line-height: 1.55;
}

.host-overview__grid,
.host-daily {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(320px, 0.9fr);
  gap: 18px;
}

.host-panel {
  display: grid;
  gap: 16px;
  padding: 20px;
}

.host-panel__actions {
  display: flex;
  justify-content: flex-end;
}

.host-panel__header,
.host-panel__header--daily {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.host-panel__header h3 {
  margin: 4px 0 0;
  color: #f4f7ff;
  font-size: clamp(1.4rem, 2vw, 2rem);
  font-family: 'Syne', sans-serif;
}

.host-panel__header p {
  margin: 8px 0 0;
  max-width: 46ch;
}

.host-panel__chart-shell {
  position: relative;
  min-height: 320px;
}

.host-panel__chart-shell.is-expanded {
  min-height: 380px;
}

.host-panel__chart-shell--doughnut {
  min-height: 280px;
}

.host-panel__chart-shell--trend {
  min-height: 360px;
}

.host-traffic {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(260px, 1.1fr);
  gap: 18px;
  align-items: center;
}

.host-traffic__list,
.host-daily__aside {
  display: grid;
  gap: 10px;
}

.host-traffic__item,
.host-daily__stat {
  display: grid;
  gap: 4px;
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(196, 206, 228, 0.08);
}

.host-traffic__item strong {
  color: #f0f4fd;
}

.host-traffic__item span {
  color: rgba(198, 207, 226, 0.72);
  font-size: 0.86rem;
}

.host-traffic__item b {
  color: #8fe2dc;
  font-size: 1.3rem;
}

.host-detail-button {
  min-height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid rgba(196, 206, 228, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: #eff4ff;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
}

.host-panel--signals {
  align-content: start;
}

.host-signal-grid {
  display: grid;
  gap: 10px;
}

.host-signal-grid--traffic {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 6px;
}

.host-signal-card {
  display: grid;
  gap: 6px;
  padding: 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(196, 206, 228, 0.08);
}

.host-signal-card small {
  color: #cfd8ea;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.host-signal-card strong {
  color: #f4f7ff;
  font-size: clamp(1.2rem, 1.8vw, 1.6rem);
  font-family: 'Syne', sans-serif;
}

.host-signal-card span,
.host-panel__hint {
  color: rgba(198, 207, 226, 0.78);
  line-height: 1.55;
}

.host-panel__hint {
  margin: 0;
}

.host-traffic-report {
  display: grid;
  gap: 16px;
}

.host-tabs {
  display: inline-flex;
  gap: 8px;
  padding: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(196, 206, 228, 0.08);
}

.host-tabs__button {
  min-height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: rgba(209, 220, 238, 0.82);
  font-weight: 700;
}

.host-tabs__button.is-active {
  background: linear-gradient(135deg, #dfe8ff, #9ee5da);
  color: #081013;
}

.host-table-wrap {
  overflow: auto;
}

.host-table {
  width: 100%;
  border-collapse: collapse;
}

.host-table th,
.host-table td {
  padding: 14px 12px;
  border-bottom: 1px solid rgba(196, 206, 228, 0.08);
  text-align: left;
  white-space: nowrap;
}

.host-table th {
  color: #d5dff2;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.host-table td {
  color: rgba(222, 230, 244, 0.88);
}

.host-empty {
  padding: 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
  color: rgba(198, 207, 226, 0.8);
}

@media (max-width: 1100px) {
  .host-overview__intro,
  .host-overview__metrics,
  .host-overview__grid,
  .host-daily,
  .host-traffic,
  .host-signal-grid--traffic {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .host-overview {
    gap: 14px;
  }

  .host-panel,
  .host-stat-card {
    padding: 16px;
    border-radius: 22px;
  }

  .host-panel__header,
  .host-panel__header--daily {
    display: grid;
  }

  .host-tabs {
    width: 100%;
    justify-content: space-between;
  }

  .host-tabs__button {
    flex: 1 1 0;
    padding: 0 10px;
    font-size: 0.84rem;
  }

  .host-panel__chart-shell,
  .host-panel__chart-shell--trend {
    min-height: 280px;
  }
}
</style>
